import { lazy } from 'react'
import type { ComponentType, LazyExoticComponent } from 'react'

/**
 * `lazy()` over a named export, safe during chunk-error recovery. When our
 * `vite:preloadError` listener claims a failed chunk (`preventDefault()`),
 * Vite's contract is to *resolve* the failed `import()` with `undefined`
 * instead of rejecting — so a bare `.then((m) => ({ default: m.X }))` crashes
 * with "Cannot read properties of undefined (reading 'X')" while the recovery
 * reload is still probing for the new asset. Re-throwing a chunk-load error
 * (recognized by {@link isChunkLoadError}) routes the render into the
 * ErrorBoundary's "updating" screen instead, unreported.
 */
export async function loadNamedExport<TModule, TProps>(
  load: () => Promise<TModule>,
  pick: (mod: TModule) => ComponentType<TProps>,
): Promise<{ default: ComponentType<TProps> }> {
  // The cast reflects Vite's runtime behavior, not the declared type.
  const mod = (await load()) as TModule | undefined
  if (mod === undefined) {
    throw new Error(
      'Failed to fetch dynamically imported module: chunk-error recovery is reloading',
    )
  }
  return { default: pick(mod) }
}

export function lazyNamed<TModule, TProps>(
  load: () => Promise<TModule>,
  pick: (mod: TModule) => ComponentType<TProps>,
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(() => loadNamedExport(load, pick))
}
